// ref: https://github.com/bartgryszko/react-native-circular-progress
// heavily modified, mainly to fix bugs with `dashedTint`: https://github.com/bartgryszko/react-native-circular-progress/issues/280
import React from 'react';
import { type StyleProp, View, type ViewStyle } from 'react-native';
import { G, Path, Svg } from 'react-native-svg';

interface CircularProgressProps {
  /**
   * Style of the entire progress container
   *
   * @type {StyleProp<ViewStyle>}
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Width and height of circle
   *
   * @type {number>}
   */
  size: number;

  /**
   * Thickness of the progress line
   *
   * @type {number}
   */
  width: number;

  /**
   * Current progress / fill
   *
   * @type {number}
   */
  fill: number;

  /**
   * Thickness of background circle
   *
   * @type {number}
   * @default width
   */
  backgroundWidth?: number;

  /**
   * Color of the progress line
   *
   * @type {string}
   * @default 'black'
   */
  tintColor?: string;
  tintColorSecondary?: string;

  /**
   * Current progress / tint transparency
   *
   * @type {boolean}
   * @default true
   */
  tintTransparency?: boolean;

  /**
   * If unspecified, no background line will be rendered
   *
   * @type {string}
   */
  backgroundColor?: string;

  /**
   * Angle from which the progress starts from
   *
   * @type {number}
   * @default 90
   */
  rotation?: number;

  /**
   * Shape used at ends of progress line.
   *
   * @type {('butt' | 'round' | 'square')}
   * @default 'butt'
   */
  lineCap?: 'butt' | 'round' | 'square';

  /**
   * Shape used at ends of progress line.
   *
   * @type {('butt' | 'round' | 'square')}
   * @default lineCap - which is 'butt'
   */
  fillLineCap?: 'butt' | 'round' | 'square';

  /**
   * If you don't want a full circle, specify the arc angle
   *
   * @type {number}
   * @default 360
   */
  arcSweepAngle?: number;

  /**
   * Pass a function as a child. It receiveds the current fill-value as an argument
   *
   * @type {Function}
   * @param {number} fill current fill-value
   * @return {JSX.Element} the element inside the circle
   */
  children: JSX.Element;

  /**
   * Style of the children container
   *
   * @type {StyleProp<ViewStyle>}
   */
  childrenContainerStyle?: StyleProp<ViewStyle>;

  /**
   * Padding applied around the circle to allow for a cap that bleeds outside its boundary
   *
   * @type {number}
   * @default 0
   */
  padding?: number;

  /**
   * Function that's invoked during rendering to draw at the tip of the progress circle
   *
   */
  renderCap?: (payload: {
    center: { x: number; y: number };
  }) => React.ReactNode;

  /**
   * Use dashed type for tint/progress line
   *
   * @type { width: number; gap: number }
   * @default '{ width: 0, gap: 0 }'
   */
  dashedTint?: { width: number; gap: number };

  /**
   * Use dashed type for background
   *
   * @type { width: number; gap: number }
   * @default '{ width: 0, gap: 0 }'
   */
  dashedBackground?: { width: number; gap: number };
}

export default class CircularProgress extends React.PureComponent<CircularProgressProps> {
  polarToCartesian(
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number
  ) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    };
  }

  circlePath(
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number
  ) {
    const start = this.polarToCartesian(x, y, radius, endAngle);
    const end = this.polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    const d = [
      'M',
      start.x,
      start.y,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y
    ];
    return d.join(' ');
  }

  clampFill = (fill: number) => Math.min(100, Math.max(0, fill));

  render() {
    const {
      size,
      width,
      backgroundWidth,
      tintColor = 'black',
      tintColorSecondary,
      backgroundColor,
      style,
      rotation = 90,
      lineCap = 'butt',
      fillLineCap = lineCap,
      arcSweepAngle = 360,
      fill,
      children,
      padding = 0,
      dashedBackground = { width: 0, gap: 0 },
      dashedTint = { width: 0, gap: 0 }
    } = this.props;

    const maxWidthCircle = backgroundWidth
      ? Math.max(width, backgroundWidth)
      : width;
    const sizeWithPadding = size / 2 + padding / 2;
    const radius = size / 2 - maxWidthCircle / 2 - padding / 2;

    const currentFillAngle = Math.round(
      (arcSweepAngle * this.clampFill(fill)) / 100
    );
    const backgroundPath = this.circlePath(
      sizeWithPadding,
      sizeWithPadding,
      radius,
      currentFillAngle,
      arcSweepAngle
    );
    const circlePath = this.circlePath(
      sizeWithPadding,
      sizeWithPadding,
      radius,
      0,
      currentFillAngle
    );

    const offset = size - maxWidthCircle * 2;

    const localChildrenContainerStyle: StyleProp<ViewStyle> = {
      position: 'absolute',
      left: maxWidthCircle + padding / 2,
      top: maxWidthCircle + padding / 2,
      width: offset,
      height: offset,
      borderRadius: offset / 2,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    };

    const strokeDasharrayTint =
      dashedTint.gap > 0
        ? Object.values(dashedTint).map((value) =>
            Number.parseInt(value.toString())
          )
        : undefined;

    const strokeDasharrayBackground =
      dashedBackground.gap > 0
        ? Object.values(dashedBackground).map((value) =>
            Number.parseInt(value.toString())
          )
        : undefined;

    return (
      <View style={style}>
        <Svg width={size + padding} height={size + padding}>
          <G
            rotation={rotation}
            originX={(size + padding) / 2}
            originY={(size + padding) / 2}
          >
            {backgroundColor && (
              <Path
                d={backgroundPath}
                stroke={backgroundColor}
                strokeWidth={backgroundWidth || width}
                strokeLinecap={lineCap}
                strokeDasharray={strokeDasharrayBackground}
                fill="transparent"
              />
            )}
            {fill > 0 && (
              <Path
                d={circlePath}
                stroke={
                  tintColorSecondary
                    ? hexBetweenColors(tintColor, tintColorSecondary, fill)
                    : tintColor
                }
                strokeWidth={width}
                strokeLinecap={fillLineCap}
                strokeDasharray={strokeDasharrayTint}
                fill="transparent"
              />
            )}
          </G>
        </Svg>
        {children && (
          <View style={localChildrenContainerStyle}>{children}</View>
        )}
      </View>
    );
  }
}

function hexBetweenColors(
  hexColor1: string,
  hexColor2: string,
  fillPercentage: number /* 0-100 */
) {
  // Convert hex to RGB
  const rgb1 = hexToRgb(hexColor1);
  const rgb2 = hexToRgb(hexColor2);

  // Calculate the intermediate color
  const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * (fillPercentage / 100));
  const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * (fillPercentage / 100));
  const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * (fillPercentage / 100));

  // Convert back to hex
  return rgbToHex(r, g, b);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: Number.parseInt(result[1], 16),
        g: Number.parseInt(result[2], 16),
        b: Number.parseInt(result[3], 16)
      }
    : { r: 0, g: 0, b: 0 };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
